import {
  getBuildVersion,
  getEnvironment,
  getErrorTimeIso,
  toPublicHost,
} from "@/lib/observability/build-info";
import {
  inferBrowserFromUserAgent,
  inferDeviceFromUserAgent,
  inferOsFromUserAgent,
} from "@/lib/observability/request-context";
import { sanitizeSentryEvent } from "@/lib/observability/sanitize";
import type { ExecutionSide } from "@/lib/observability/types";

type MutableEvent = Record<string, unknown>;

function toRecord(value: unknown): MutableEvent | undefined {
  if (!value || typeof value !== "object") return undefined;
  return value as MutableEvent;
}

function toStringValue(value: unknown): string | undefined {
  if (value == null) return undefined;
  if (typeof value === "string") return value;
  return String(value);
}

function getHeaderFromEvent(
  event: MutableEvent,
  key: string,
): string | undefined {
  const request = toRecord(event.request);
  if (!request) return undefined;

  const headers = toRecord(request.headers);
  if (!headers) return undefined;

  const value = headers[key] ?? headers[key.toLowerCase()];
  if (Array.isArray(value)) {
    const [first] = value;
    return toStringValue(first);
  }

  return toStringValue(value);
}

function firstCsvValue(value?: string): string | undefined {
  if (!value) return undefined;
  const [first] = value.split(",");
  return first?.trim();
}

function getUserIpFromEvent(event: MutableEvent): string {
  const user = toRecord(event.user);
  const userIp = toStringValue(user?.ip_address);
  if (userIp) return userIp;

  const cfIp = getHeaderFromEvent(event, "cf-connecting-ip");
  if (cfIp) return cfIp;

  const xff = firstCsvValue(getHeaderFromEvent(event, "x-forwarded-for"));
  if (xff) return xff;

  const xri = getHeaderFromEvent(event, "x-real-ip");
  if (xri) return xri;

  return "unknown";
}

function getCountryFromEvent(event: MutableEvent): string {
  return (
    getHeaderFromEvent(event, "cf-ipcountry") ||
    getHeaderFromEvent(event, "x-country-code") ||
    getHeaderFromEvent(event, "x-country") ||
    "unknown"
  );
}

function getDnsHostFromEvent(event: MutableEvent): string {
  const host =
    getHeaderFromEvent(event, "x-forwarded-host") ||
    getHeaderFromEvent(event, "host");

  if (host) return toPublicHost(host);

  const request = toRecord(event.request);
  const requestUrl = toStringValue(request?.url);
  if (requestUrl) {
    return toPublicHost(requestUrl);
  }

  if (typeof window !== "undefined") {
    return window.location.host || "unknown";
  }

  return "unknown";
}

function getUserAgentFromEvent(event: MutableEvent): string {
  const ua = getHeaderFromEvent(event, "user-agent");
  if (ua) return ua;

  if (typeof navigator !== "undefined") {
    return navigator.userAgent;
  }

  return "unknown";
}

function getUserIdFromEvent(event: MutableEvent): string {
  const user = toRecord(event.user);
  const userId = toStringValue(user?.id);
  return userId || "anonymous";
}

function getUserEmailFromEvent(event: MutableEvent): string {
  const user = toRecord(event.user);
  const userEmail = toStringValue(user?.email);
  return userEmail || "anonymous";
}

export function enrichAndSanitizeSentryEvent<T>(
  event: T,
  side: ExecutionSide,
): T {
  const nextEvent = sanitizeSentryEvent({
    ...(event as MutableEvent),
  }) as MutableEvent;

  const userAgent = getUserAgentFromEvent(nextEvent);

  // Check for custom platform header from native/desktop apps
  const platformHeader = getHeaderFromEvent(nextEvent, "x-vidseeds-platform");
  const userPlatform =
    platformHeader === "ios" ||
    platformHeader === "android" ||
    platformHeader === "desktop"
      ? `${platformHeader}_app`
      : "web";

  const metadata = {
    build_version: getBuildVersion(),
    environment: getEnvironment(),
    dns_host: getDnsHostFromEvent(nextEvent),
    execution_side: side,
    error_time_iso: getErrorTimeIso(),
    user_id: getUserIdFromEvent(nextEvent),
    user_email: getUserEmailFromEvent(nextEvent),
    user_ip: getUserIpFromEvent(nextEvent),
    user_browser: inferBrowserFromUserAgent(userAgent),
    user_device: inferDeviceFromUserAgent(userAgent),
    user_os: inferOsFromUserAgent(userAgent),
    user_platform: userPlatform,
    user_country: getCountryFromEvent(nextEvent),
  };

  const existingTags = toRecord(nextEvent.tags) || {};
  nextEvent.tags = {
    ...existingTags,
    build_version: metadata.build_version,
    environment: metadata.environment,
    dns_host: metadata.dns_host,
    execution_side: metadata.execution_side,
    user_country: metadata.user_country,
    user_device: metadata.user_device,
    user_browser: metadata.user_browser,
    user_os: metadata.user_os,
    user_platform: metadata.user_platform,
  };

  const existingContexts = toRecord(nextEvent.contexts) || {};
  const existingErrorMetadata = toRecord(existingContexts.error_metadata) || {};

  nextEvent.contexts = {
    ...existingContexts,
    error_metadata: {
      ...existingErrorMetadata,
      ...metadata,
    },
  };

  return nextEvent as T;
}
